/* WebComponentPartnerWizard component */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging -- disabled to allow to inherit from AbstractComponent */
import { Component, Element, Event, EventEmitter, h, Host, Prop } from '@stencil/core'; // eslint-disable-line @typescript-eslint/no-unused-vars
import { AbstractComponent } from '@cewe-designsystem/abstract';
import UtilBrowserDetection from '@cewe-designsystem/util_browser-detection';
import { getHpsKeyAccount } from './utils/utils';
import { SoftwareLinkClicked } from './types/events';
import { ORDER_CHANNEL_KEY } from './types/orderChannels';

/**
 *
 */
@Component({
  tag: 'cwc-partner-wizard-software-download',
  styleUrl: './styles/cwc-partner-wizard-software-download.scss',
  shadow: true,
})
export class PartnerWizardSoftwareDownloadComponent {
  @Element() element: HTMLElement;

  @Prop() productId: number;
  @Prop() keyAccount: number;
  /** the link for the download page of the hps software */
  @Prop() downloadPageLink: string = '';
  /** (optional) when the session allows a mapping then the mapKeyAccount will be used on the own key account */
  @Prop() mapKeyAccount: number | null;
  /** text of the download button */
  @Prop() textButton: string;

  private downloadLink: URL;

  private browserDetection: UtilBrowserDetection;

  /**
   * Event thrown when the user clicked on the hps/mps link to download the software
   *
   * @docs
   * @event on:partner-wizard:software-link-clicked
   */
  @Event({
    eventName: 'on:partner-wizard:software-link-clicked',
  })
  onSoftwareLinkClicked: EventEmitter<SoftwareLinkClicked>;

  /**
   * Called every time the component is connected to the DOM.
   * When the component is first connected, this method is called before componentWillLoad.
   * It's important to note that this method can be called more than once, everytime, the element is attached or moved in the DOM.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec
   */
  connectedCallback() {
    this.browserDetection = new UtilBrowserDetection();

    const keyAccount = this.getKeyAccount();

    this.downloadLink = new URL(this.downloadPageLink, window.location.href);
    this.downloadLink.searchParams.set('keyAccount', keyAccount.toString());
    this.downloadLink.searchParams.set('product', this.productId.toString());
  }

  /**
   * Called every time the component is disconnected from the DOM, ie, it call be dispatched more than once
   * DO not confuse with a "onDestroy" kind of event.
   * This lifecycle hook follows the same semantics as the one described by the Custom Elements Spec.
   */
  disconnectedCallback() {}

  /**
   * Called once just after the component is first connected to the DOM.
   * A promise can be returned, that can be used to wait for the first render.
   */
  componentWillLoad() {}

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  componentDidLoad() {}

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {}

  /**
   * Called after every render().
   */
  componentDidRender() {}

  /**
   * Called when the component is about to be updated because some Prop() or State() changed.
   * It's never called during the first render().
   * A promise can be returned, that can be used to wait for the next render.
   */
  componentWillUpdate() {}

  /**
   * Called just after the component updates. It's never called during the first render().
   */
  componentDidUpdate() {}

  private getKeyAccount(): number {
    const keyAccountParameter = new URL(window.location.href).searchParams.get('keyAccount');

    if (keyAccountParameter && keyAccountParameter.match(/^\d+$/)) {
      return Number(keyAccountParameter);
    }

    return getHpsKeyAccount(this.keyAccount, this.mapKeyAccount);
  }

  /**
   * render
   */
  render() {
    return (
      <Host>
        <a
          onClick={() =>
            this.onSoftwareLinkClicked.emit({
              keyAccount: this.getKeyAccount(),
              type: ORDER_CHANNEL_KEY.DESKTOP_SOFTWARE,
              os: this.browserDetection.getOs(),
              external: false,
              target: this.downloadLink.toString(),
            })
          }
          href={this.downloadLink.toString()}
          part={'clicktarget'}
        >
          <span class="button" part={'button'}>
            {this.textButton}
          </span>
        </a>
      </Host>
    );
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(PartnerWizardSoftwareDownloadComponent.prototype, AbstractComponent.prototype);
export interface PartnerWizardSoftwareDownloadComponent extends AbstractComponent {}
